<template>
  <section
    class="flex flex-col gap-3 rounded-lg border border-lightGray30 bg-white p-3.5"
    :data-testid="testId"
  >
    <div>
      <h3 class="m-0 text-sm font-bold text-textPrimary">Registrar contato</h3>
      <p class="m-0 text-xs text-neutral50">{{ doctorName }} · #{{ doctorStationId }}</p>
    </div>

    <div class="flex flex-col gap-1">
      <span class="text-[11px] uppercase tracking-wider text-neutral50">Canal</span>
      <div class="flex flex-wrap gap-1">
        <button
          v-for="(label, value) in PRIORITY_CONTACT_CHANNEL_LABELS"
          :key="value"
          type="button"
          class="rounded-full border px-2.5 py-1 text-[13px]"
          :class="
            channel === value
              ? 'border-primary bg-primary text-white'
              : 'border-lightGray30 bg-white text-neutral50'
          "
          :aria-pressed="channel === value"
          :data-testid="`${testId}-channel-${value}`"
          @click="channel = value"
        >
          {{ label }}
        </button>
      </div>
    </div>

    <div class="flex flex-col gap-1">
      <label class="text-[11px] uppercase tracking-wider text-neutral50" :for="`${testId}-note`">
        Observação (opcional)
      </label>
      <textarea
        :id="`${testId}-note`"
        v-model="note"
        rows="2"
        :maxlength="PRIORITY_CONTACT_NOTE_MAX_LENGTH"
        class="w-full rounded-md border border-lightGray30 px-2 py-1 text-[13px] text-textPrimary"
        :data-testid="`${testId}-note`"
      />
      <span
        class="self-end text-[11px] tabular-nums text-neutral50"
        :data-testid="`${testId}-note-counter`"
      >
        {{ note.length }}/{{ PRIORITY_CONTACT_NOTE_MAX_LENGTH }}
      </span>
    </div>

    <div class="flex flex-wrap items-center gap-2">
      <button
        type="button"
        class="rounded-md border border-primary bg-primary px-3 py-1.5 text-sm font-medium text-white"
        :disabled="isSaving"
        :data-testid="`${testId}-submit`"
        @click="onSubmit"
      >
        Registrar contato
      </button>
      <button
        type="button"
        class="rounded-md border border-lightGray30 px-3 py-1.5 text-sm font-medium text-textPrimary"
        :disabled="isSaving"
        :data-testid="`${testId}-cancel`"
        @click="emit('cancel')"
      >
        Cancelar
      </button>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref } from 'vue'

import type { PriorityContactChannel } from '@/Network/Types/Responses/PriorityPaymentsResponses'

import { PRIORITY_CONTACT_CHANNEL_LABELS, PRIORITY_CONTACT_NOTE_MAX_LENGTH } from '../constants'

const props = withDefaults(
  defineProps<{
    doctorStationId: number
    doctorName: string
    /** Quem chega por "Copiar mensagem" já vem com WhatsApp marcado. */
    defaultChannel?: PriorityContactChannel
    isSaving?: boolean
    testId?: string
  }>(),
  { defaultChannel: 'WHATSAPP', isSaving: false, testId: 'priority-contact-form' }
)

const emit = defineEmits<{
  submit: [payload: { doctorStationId: number; channel: PriorityContactChannel; note?: string }]
  cancel: []
}>()

const channel = ref<PriorityContactChannel>(props.defaultChannel)
const note = ref('')

/**
 * Observação em branco NÃO vira `note: ''`: o contrato a trata como ausente, e
 * gravar string vazia encheria o histórico de cobrança de linhas que parecem ter
 * anotação e não têm.
 */
function onSubmit(): void {
  const trimmed = note.value.trim()
  emit('submit', {
    doctorStationId: props.doctorStationId,
    channel: channel.value,
    ...(trimmed ? { note: trimmed } : {})
  })
}
</script>
